import React, { useEffect, useState } from 'react';
import { BarChart, DonutChart } from '@mantine/charts';
import { Group, Paper, Tabs, Text, Title, useMantineColorScheme } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { ICurrency } from '../../../../libraries/currencies';
import { formatCurrAmount, roundDownToPrevIncrement, roundUpToNextIncrement } from '../../../utils/script';
import {
  DayliTotals,
  fetchDailyTotalsDays,
  fetchDailyTotalsMonth,
  fetchDailyTotalsWeek,
  Totals
} from '../../../../libraries/daily-totals';
import dayjs from 'dayjs';
import './styles.scss';

type BarChartCompProps = {
  data?: DayliTotals[];
  currency?: ICurrency;
};

export const BarChartComp: React.FC<BarChartCompProps> = ({ data = [], currency }) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const selectedCid = currency?.cid;

  const hasData = data.length > 0 && data.some(day => day.totals.some(t => t.cid === selectedCid && t.total > 0));

  if (!hasData) {
    return (
      <div className="bar-chart flex flex-col items-center justify-center h-[300px] text-gray-500">
        <Title order={3}>{`${lang.DASHBOARD_AMOUNT_PER_DAY} (${currency?.currencyStr})`}</Title>
        <Title order={2} className="mt-4 text-lg font-medium">
          {lang.EQUIPMENT_NO_DATA}
        </Title>
      </div>
    );
  }

  const today = dayjs();
  // Get Monday of this week
  const mondayOfWeek = today.subtract((today.day() + 6) % 7, 'day');
  const chartData: { day: number; totals: Totals[]; amount: number; dayLabel: string; weekday: string }[] = [];

  for (let i = 0; i < 7; i++) {
    const currentDay = mondayOfWeek.add(i, 'day');
    const dayNum = Number(currentDay.format('YYYYMMDD'));

    const dayData = data.find(d => d.day === dayNum);
    const matchingTotal = dayData?.totals.find(t => t.cid === selectedCid);

    chartData.push({
      day: dayNum,
      totals: dayData?.totals ?? [{ cid: selectedCid ?? 0, total: 0, paid: 0 }],
      amount: matchingTotal?.total ?? 0,
      dayLabel: currentDay.format('YYYY/MM/DD'),
      weekday: currentDay.format('ddd')
    });
  }

  const avg =
    selectedCid && chartData.length > 0 ? chartData.reduce((acc, d) => acc + d.amount, 0) / chartData.length : 0;

  const maxValue = Math.max(...chartData.map(d => d.amount), 0);
  const minValue = Math.min(...chartData.map(d => d.amount), 0);

  const yAxisMin = roundDownToPrevIncrement(minValue, 500);
  const yAxisMax = roundUpToNextIncrement(maxValue, 500);

  return (
    <div className="bar-chart">
      <Title order={3}>{`${lang.DASHBOARD_AMOUNT_PER_DAY} (${currency?.currencyStr})`}</Title>

      <BarChart
        h={300}
        data={chartData}
        dataKey="dayLabel"
        strokeDasharray="0"
        tooltipAnimationDuration={200}
        yAxisProps={{ domain: [yAxisMin, yAxisMax] }}
        tooltipProps={{
          content: ({ payload }) => {
            if (!payload || payload.length === 0) return null;
            const dayData = payload[0].payload;
            return (
              <Paper px="md" py="sm" withBorder shadow="md" radius="md" className="chart-tooltip">
                <Title>{dayData.weekday}</Title>
                {payload.map((item: any) => (
                  <Text key={item.name}>Amount: {formatCurrAmount(item.value, currency, true, true)}</Text>
                ))}
              </Paper>
            );
          }
        }}
        series={[{ name: 'amount', color: 'blue.6' }]}
        referenceLines={[
          {
            y: avg,
            label: `Avg:\n${formatCurrAmount(Math.round(avg), currency, true, true)}`,
            labelPosition: 'insideBottomRight',
            strokeDasharray: '5 5'
          }
        ]}
      />
    </div>
  );
};

type DonutCardProps = {
  label?: string;
  data?: DayliTotals[];
  cid?: number;
  currency?: ICurrency;
  maxValue?: number;
  mode?: 'total' | 'today';
  maxSuffix?: string;
};

export const DonutCard: React.FC<DonutCardProps> = ({
  label,
  data = [],
  cid,
  currency,
  maxValue = 50000,
  mode = 'total',
  maxSuffix = ''
}) => {
  const today = new Date();
  const todayNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const value = cid
    ? mode === 'today'
      ? data.find(dayItem => Number(dayItem.day) === todayNum)?.totals.find(t => t.cid === cid)?.total || 0
      : data.reduce((acc, dayItem) => acc + (dayItem.totals.find(t => t.cid === cid)?.total || 0), 0)
    : 0;

  const percentage = Math.min((value / maxValue) * 100, 100);

  const color = getColor(value, maxValue, isDark);

  const chartData = [
    { name: 'Used', value, color },
    { name: 'Remaining', value: Math.max(maxValue - value, 0), color: '#ccccccff' }
  ];

  function getThresholdColor(percent: number, isDark: boolean): string {
    if (percent > 0.95) {
      return isDark ? '#ff6b6b' : '#E3353F';
    }

    if (percent > 0.85) {
      return isDark ? '#ffb347' : '#e97b15ff';
    }

    return isDark ? '#59e771' : '#137733';
  }

  function getColor(value: number, max: number, isDark: boolean): string {
    const percent = Math.min(value / max, 1);
    return getThresholdColor(percent, isDark);
  }

  return (
    <Group className="donut-chart-wrapper">
      <div className="donut-chart-container">
        <DonutChart
          data={chartData}
          size={100}
          thickness={18}
          withLabels={false}
          withTooltip={false}
          paddingAngle={2}
          chartLabel={`${Math.round(percentage)}%`}
        />
      </div>

      <div className="donut-texts">
        <Title>{label}</Title>
        <Text className="donut-value" style={{ color }}>
          {formatCurrAmount(value, currency)}
        </Text>
        <Text className="donut-max-value">{`/ ${formatCurrAmount(maxValue, currency)}`}</Text>
        <Text className="donut-max-value">{maxSuffix}</Text>
      </div>
    </Group>
  );
};

type DonutCardWithTabsProps = {
  activeCid: number;
  currencies?: ICurrency[];
};

const PERIODS: Array<'last7' | 'prevWeek' | 'last30' | 'prevMonth'> = ['last7', 'prevWeek', 'last30', 'prevMonth'];

export const DonutCardWithTabs: React.FC<DonutCardWithTabsProps> = ({ currencies, activeCid }) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const entity = useSelector<AppState, number>(state => state.login.entity?.entid || 0);
  const currency = currencies?.find(c => c.cid === activeCid);
  const [period, setPeriod] = useState<'last7' | 'prevWeek' | 'last30' | 'prevMonth'>('last7');
  const [periodData, setPeriodData] = useState<DayliTotals[]>([]);

  useEffect(() => {
    if (!entity) return;

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        let data: DayliTotals[] = [];
        switch (period) {
          case 'prevWeek':
            data = await fetchDailyTotalsWeek(controller, entity);
            break;
          case 'last30':
            data = await fetchDailyTotalsDays(controller, entity, 30);
            break;
          case 'prevMonth':
            data = await fetchDailyTotalsMonth(controller, entity);
            break;
          case 'last7':
          default:
            data = await fetchDailyTotalsDays(controller, entity, 7);
        }
        setPeriodData(data);
      } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          console.error('Error fetching totals:', err);
        }
      } finally {
        controller.abort();
      }
    };

    fetchData();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, activeCid]);

  const periodLabels: Record<(typeof PERIODS)[number], string> = {
    last7: lang.DASHBOARD_AVG_7_DAYS || 'Last 7 Days',
    prevWeek: lang.DASHBOARD_AVG_PREV_WEEK || 'Previous Week',
    last30: lang.DASHBOARD_AVG_30_DAYS || 'Last 30 Days',
    prevMonth: lang.DASHBOARD_AVG_PREV_MONTH || 'Previous Month'
  };

  return (
    <Tabs
      value={period}
      onChange={val => val && setPeriod(val as any)}
      keepMounted={false}
      radius="sm"
      className="donut-charts-tabs"
    >
      {PERIODS.map(p => (
        <Tabs.Panel key={p} value={p}>
          <DonutCard
            label={`${periodLabels[p]}`}
            data={periodData || []}
            cid={activeCid}
            currency={currency}
            maxValue={50000}
            mode="total"
          />
        </Tabs.Panel>
      ))}

      <Tabs.List>
        {PERIODS.map(p => (
          <Tabs.Tab key={p} value={p}>
            {periodLabels[p]}
          </Tabs.Tab>
        ))}
      </Tabs.List>
    </Tabs>
  );
};
